import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent, Divider, Grid,
  IconButton, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import { getUsersByCompany } from 'services/firebaseServices';
import { getProfile } from 'utilities';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { fireStoreDB } from 'services';
import {
  CHECKS, CRITICAL_CHECKS, calcScore, scoreColor, scoreBand, missingCriticalLabels,
} from '../dutyOfCare';

export default function HealthCompleteness() {
  const [employees, setEmployees] = useState([]);
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // employee for the record dialog

  useEffect(() => {
    const load = async () => {
      try {
        const profile = getProfile();
        if (!profile?.uid) return;
        const q = query(collection(fireStoreDB, 'businesses'), where('userId', '==', profile.uid));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const biz = snap.docs[0].data();
          setBusinessData(biz);
          const result = await getUsersByCompany(biz.companyName);
          setEmployees(result);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const scored = employees
    .map((emp) => ({ ...emp, score: calcScore(emp) }))
    .sort((a, b) => a.score - b.score); // lowest first — needs attention

  const avg = scored.length ? Math.round(scored.reduce((s, e) => s + e.score, 0) / scored.length) : 0;
  const fullyComplete = scored.filter((e) => e.score === 100).length;
  const needsAttention = scored.filter((e) => e.score < 50).length;

  const companyName = businessData?.companyName || 'Company';
  const generatedAt = new Date().toLocaleString();

  // ── Exportable ISO 31030 report ───────────────────────────────────────────────
  const downloadCSV = () => {
    const meta = [
      ['Duty of Care / ISO 31030 Readiness Report'],
      ['Company', companyName],
      ['Generated', generatedAt],
      ['Employees', String(scored.length)],
      ['Average completeness', `${avg}%`],
      [],
    ];
    const header = ['Employee', 'Email', 'Completeness %', 'Status', 'Missing critical data', 'Missing fields'];
    const rows = scored.map((e) => [
      `${e.firstName || ''} ${e.lastName || ''}`.trim(),
      e.email || '',
      String(e.score),
      scoreBand(e.score).label,
      missingCriticalLabels(e).join('; ') || 'None',
      CHECKS.filter((c) => !c.check(e)).map((c) => c.label).join('; ') || 'None',
    ]);
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [...meta, header, ...rows].map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `duty-of-care-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printReport = () => {
    const rowsHtml = scored.map((e) => `
      <tr>
        <td>${e.firstName || ''} ${e.lastName || ''}</td>
        <td>${e.email || ''}</td>
        <td style="text-align:center">${e.score}%</td>
        <td>${scoreBand(e.score).label}</td>
        <td>${missingCriticalLabels(e).join(', ') || '—'}</td>
      </tr>`).join('');
    const html = `
      <html><head><title>Duty of Care Report — ${companyName}</title>
      <style>
        body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#101828;padding:32px;}
        h1{font-size:20px;margin:0 0 4px;} .muted{color:#667085;font-size:13px;}
        .summary{display:flex;gap:24px;margin:20px 0;}
        .stat{border:1px solid #EAECF0;border-radius:10px;padding:12px 16px;}
        .stat b{font-size:22px;display:block;}
        table{width:100%;border-collapse:collapse;margin-top:12px;font-size:13px;}
        th,td{text-align:left;padding:8px;border-bottom:1px solid #EAECF0;}
        th{color:#667085;text-transform:uppercase;font-size:11px;}
      </style></head><body>
      <h1>Duty of Care / ISO 31030 Readiness Report</h1>
      <div class="muted">${companyName} · Generated ${generatedAt}</div>
      <div class="summary">
        <div class="stat"><b>${avg}%</b><span class="muted">Avg. completeness</span></div>
        <div class="stat"><b>${fullyComplete}</b><span class="muted">Fully complete</span></div>
        <div class="stat"><b>${needsAttention}</b><span class="muted">Needs attention</span></div>
        <div class="stat"><b>${scored.length}</b><span class="muted">Employees</span></div>
      </div>
      <table><thead><tr><th>Employee</th><th>Email</th><th>Completeness</th><th>Status</th><th>Missing critical data</th></tr></thead>
      <tbody>${rowsHtml}</tbody></table>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <Box sx={{ width: '100%', p: { xs: 1, md: 2 } }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 2, mb: 3, flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#101828' }}>
            Compliance &amp; Duty of Care
          </Typography>
          <Typography variant="body2" sx={{ color: '#667085', mt: 0.5 }}>
            Workforce health-profile readiness, aligned to ISO 31030.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined" startIcon={<DownloadIcon />} onClick={downloadCSV} disabled={!scored.length}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: '#D0D5DD', color: '#344054' }}
          >
            Export CSV
          </Button>
          <Button
            variant="contained" startIcon={<PrintIcon />} onClick={printReport} disabled={!scored.length}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, bgcolor: '#32ABF8', '&:hover': { bgcolor: '#1E91D6' } }}
          >
            Report / PDF
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { icon: <AssignmentIcon />, label: 'Avg. Completeness', value: loading ? '…' : `${avg}%`, color: '#3b82f6' },
          { icon: <CheckCircleIcon />, label: 'Fully Complete', value: loading ? '…' : fullyComplete, color: '#10b981' },
          { icon: <WarningAmberIcon />, label: 'Needs Attention (<50%)', value: loading ? '…' : needsAttention, color: '#ef4444' },
        ].map(({ icon, label, value, color }) => (
          <Grid item xs={12} sm={4} key={label}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #EAECF0', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
              </Box>
              <Typography variant="body2" sx={{ color: '#667085', fontWeight: 500 }}>{label}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#101828', lineHeight: 1 }}>{value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #EAECF0' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#101828', mb: 0.5 }}>Employee Records</Typography>
        <Typography variant="body2" sx={{ color: '#667085', mb: 3 }}>
          Sorted by completeness — click a row to open the full Duty of Care record.
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : scored.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <AssignmentIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
            <Typography color="text.secondary">No employees found.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Employee', 'Completeness', 'Score', 'Missing Fields'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, bgcolor: '#F9FAFB', color: '#667085', border: 'none', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {scored.map((emp) => {
                  const missing = CHECKS.filter((c) => !c.check(emp)).map((c) => c.label);
                  return (
                    <TableRow
                      key={emp.id}
                      hover
                      onClick={() => setSelected(emp)}
                      sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#101828' }}>
                          {emp.firstName} {emp.lastName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#667085' }}>{emp.email}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        <LinearProgress
                          variant="determinate"
                          value={emp.score}
                          sx={{ height: 8, borderRadius: 4, bgcolor: '#f3f4f6', '& .MuiLinearProgress-bar': { bgcolor: scoreColor(emp.score), borderRadius: 4 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={`${emp.score}%`} size="small" sx={{ fontWeight: 700, bgcolor: `${scoreColor(emp.score)}18`, color: scoreColor(emp.score) }} />
                      </TableCell>
                      <TableCell>
                        {missing.length === 0 ? (
                          <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600 }}>Complete</Typography>
                        ) : (
                          <Tooltip title={missing.join(', ')} arrow>
                            <Typography variant="caption" sx={{ color: '#667085', cursor: 'default' }}>
                              {missing.slice(0, 2).join(', ')}{missing.length > 2 ? ` +${missing.length - 2} more` : ''}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <DutyOfCareRecordDialog
        employee={selected}
        company={companyName}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
}

// ── Per-employee Duty of Care record ──────────────────────────────────────────
function DutyOfCareRecordDialog({ employee, company, onClose }) {
  if (!employee) return null;
  const score = calcScore(employee);
  const band = scoreBand(score);
  const criticalMissing = missingCriticalLabels(employee);

  const CheckRow = ({ c }) => {
    const ok = c.check(employee);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #F2F4F7' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {ok
            ? <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
            : <CancelIcon sx={{ color: '#ef4444', fontSize: 18 }} />}
          <Typography variant="body2" sx={{ color: '#344054' }}>{c.label}</Typography>
          {c.critical && (
            <Chip label="Critical" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#FEF3F2', color: '#D92D20' }} />
          )}
        </Box>
        <Typography variant="caption" sx={{ color: ok ? '#10b981' : '#98A2B3', fontWeight: 600 }}>
          {ok ? 'On file' : 'Missing'}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog open={!!employee} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ p: 3, pb: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: '#EAF6FE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HealthAndSafetyIcon sx={{ color: '#32ABF8' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#101828', lineHeight: 1.2 }}>
              {employee.firstName} {employee.lastName}
            </Typography>
            <Typography variant="caption" sx={{ color: '#667085' }}>
              Duty of Care record · {company}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <DialogContent sx={{ pt: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, bgcolor: '#F9FAFB', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: '#667085' }}>Readiness</Typography>
            <LinearProgress variant="determinate" value={score}
              sx={{ height: 8, borderRadius: 4, mt: 0.5, bgcolor: '#EAECF0', '& .MuiLinearProgress-bar': { bgcolor: scoreColor(score), borderRadius: 4 } }} />
          </Box>
          <Chip label={`${score}% · ${band.label}`} sx={{ fontWeight: 700, bgcolor: `${band.color}18`, color: band.color }} />
        </Box>

        {criticalMissing.length > 0 && (
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#FEF3F2', border: '1px solid #FEE4E2', mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            <WarningAmberIcon sx={{ color: '#D92D20', fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: '#B42318', fontWeight: 500 }}>
              Missing critical emergency data: {criticalMissing.join(', ')}
            </Typography>
          </Box>
        )}

        <Typography variant="caption" sx={{ color: '#98A2B3', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Critical emergency data
        </Typography>
        <Box sx={{ mb: 2, mt: 0.5 }}>
          {CRITICAL_CHECKS.map((c) => <CheckRow key={c.label} c={c} />)}
        </Box>

        <Typography variant="caption" sx={{ color: '#98A2B3', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          All profile fields
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          {CHECKS.filter((c) => !c.critical).map((c) => <CheckRow key={c.label} c={c} />)}
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" sx={{ color: '#98A2B3' }}>
          Record generated {new Date().toLocaleString()} · reflects the employee’s current profile.
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
